// src/pages/Calibration/CalibrationRegister.jsx
//
// AISC §1.14 Calibration Phase 1 — register list with KPIs + filters.
// Closes GAP-AISC207-003 source-to-build path; UI surface for §2.7 audit walk.

import React, { useEffect, useState, useCallback } from "react";
import PageGuide from "../../components/PageGuide";
import {
  Container, Stack, Typography, Card, CardContent, Grid,
  TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  Select, MenuItem, FormControl, InputLabel, TextField, Button,
  Breadcrumbs, Box, CircularProgress, Alert,
} from "@mui/material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";

import {
  calibrationUnitsApi,
  STATUS_LABELS,
  CATEGORY_LABELS,
  daysUntil,
} from "../../utils/calibrationApi";
import CalibrationStatusBadge from "../../components/CalibrationStatusBadge/CalibrationStatusBadge";
import { useSelector } from "react-redux";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";

import SmartTable from "../../components/SmartTable/SmartTable";
const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "in_cal", label: "In-Cal" },
  { value: "due_soon", label: "Due Soon" },
  { value: "overdue", label: "Overdue" },
  { value: "rejected", label: "Rejected" },
  { value: "out_of_service", label: "Out of Service" },
];

export default function CalibrationRegister() {
  // S-71 (2026-10-07): calibration writes are shown only with manage_calibration (Admin, Quality
  // Manager, Quality Control Inspector). The server refuses them without it (403).
  const { loginUser } = useSelector((state) => state.userSlice);
  const canManageCalibration = hasPermission(loginUser?.decodedToken?.permissions || [], PERMISSIONS.MANAGE_CALIBRATION);
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const [unitsRes, kpisRes] = await Promise.all([
        calibrationUnitsApi.list(params),
        calibrationUnitsApi.kpis(),
      ]);
      setUnits(unitsRes?.data?.data || []);
      setKpis(kpisRes?.data?.data || null);
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || "Failed to load calibration register";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = units.filter((u) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (u.serial_number || "").toLowerCase().includes(s) ||
      (u.asset_tag || "").toLowerCase().includes(s) ||
      (u.make || "").toLowerCase().includes(s) ||
      (u.model || "").toLowerCase().includes(s) ||
      (u.instrument?.name || "").toLowerCase().includes(s)
    );
  });

  return (
    <Container maxWidth="xl">
      <Helmet><title>Calibration Register | SFM</title></Helmet>

      <Breadcrumbs sx={{ mb: 1 }}>
        <RouterLink to="/dashboard/app" style={{ color: "inherit" }}>Home</RouterLink>
        <Typography color="text.primary">Calibration</Typography>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4">Calibration Register</Typography>
          <Typography variant="body2" color="text.secondary">
            AISC 207-25 §2.7 — per-instrument calibration cycle, NIST-traceable, with audit trail.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate("/calibration/catalog")}>
            Manage Catalog
          </Button>
          {canManageCalibration && (
            <Button variant="contained" onClick={() => navigate("/calibration/units/new")}>
              + Add Instrument Unit
            </Button>
          )}
        </Stack>
      </Stack>
      <PageGuide
        why="Keep inspection equipment in calibration — so the measurements you rely on are traceable and trusted."
        who="The QM and whoever manages inspection equipment."
        what="A per-instrument calibration register: cycle, due dates, and NIST-traceable certificates."
        how="Add an instrument and record its calibration and due date; the catalog defines instrument types."
        when="When equipment is calibrated, and before it's used if it's past due."
        standard="AISC 207-25 §2.7 · ISO/IEC 17025 §6.4"
      />

      {/* KPI Cards */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <Card><CardContent>
            <Typography variant="caption" color="text.secondary">IN SERVICE</Typography>
            <Typography variant="h4">{kpis?.in_service ?? "—"}</Typography>
            <Typography variant="caption" color="text.secondary">across instrument types</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: kpis?.due_soon > 0 ? "warning.light" : undefined }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">DUE IN 30 DAYS</Typography>
              <Typography variant="h4" color={kpis?.due_soon > 0 ? "warning.dark" : "text.primary"}>
                {kpis?.due_soon ?? "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {kpis?.due_soon > 0 ? "review and schedule" : "all current"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: kpis?.overdue > 0 ? "error.light" : undefined }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">OVERDUE</Typography>
              <Typography variant="h4" color={kpis?.overdue > 0 ? "error.dark" : "text.primary"}>
                {kpis?.overdue ?? "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {kpis?.overdue > 0 ? "action required — see AISC §2.7.3" : "no overdue items"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Toolbar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUS_FILTERS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search by serial / asset tag / make / model"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 320 }}
          />
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <SmartTable tableId="calibration-register" lockedColumns={["Status", "Instrument", "Next Due"]} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Instrument</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Serial #</TableCell>
                <TableCell>Make / Model</TableCell>
                <TableCell>Last Cal</TableCell>
                <TableCell>Next Due</TableCell>
                <TableCell>Days</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  No instrument units. Add one via "+ Add Instrument Unit" — requires an instrument type in the catalog first.
                </TableCell></TableRow>
              ) : filtered.map((u) => {
                const days = daysUntil(u.next_due_date);
                const status = u.effective_status || u.status;
                return (
                  <TableRow
                    key={u.id}
                    hover
                    sx={{ cursor: "pointer", bgcolor: status === "overdue" ? "error.lighter" : undefined }}
                    onClick={() => navigate(`/calibration/units/${u.id}`)}
                  >
                    <TableCell><CalibrationStatusBadge status={status} /></TableCell>
                    <TableCell>{u.instrument?.name || `#${u.instrument_id}`}</TableCell>
                    <TableCell>{CATEGORY_LABELS[u.instrument?.category] || u.instrument?.category || "—"}</TableCell>
                    <TableCell><strong>{u.serial_number}</strong></TableCell>
                    <TableCell>{[u.make, u.model].filter(Boolean).join(" / ") || "—"}</TableCell>
                    <TableCell>{u.last_calibration_date || "—"}</TableCell>
                    <TableCell>{u.next_due_date || "—"}</TableCell>
                    <TableCell sx={{ color: days != null && days < 0 ? "error.main" : days != null && days <= 30 ? "warning.dark" : undefined, fontWeight: days != null && days < 0 ? 600 : 400 }}>
                      {days != null ? (days >= 0 ? `+${days}` : days) : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </SmartTable>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {filtered.length} of {units.length} instrument units. AISC 207-25 §2.7 evidence: each row's history accessible via click; CofC PDFs cross-referenced via Documents Library.
      </Typography>
    </Container>
  );
}
